import { ICampaign, IDevice } from '../interfaces';
import { downloadScreenList } from './pricing';

const MAX_HOURS = 16; // 7 - 22
export const MAX_SLOTS = 5;

export const getWeekNumber = (d: Date) => {
    d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));

    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

    return weekNo;
};

export const dateFromWeekNumber = (weekNumber: number, date: Date) => {
    const start = new Date(date.getFullYear(), 0, 1 + (weekNumber - 1) * 7);

    if (start.getDay() <= 4) {
        start.setDate(start.getDate() - start.getDay() + 1);
    } else {
        start.setDate(start.getDate() + 8 - start.getDay());
    }

    return start;
};

export const generateCampaignObject = (campaign: any, devices: any) => {
    const signs: { [key: string]: number } = {};
    const signsOrdered: { [key: string]: number } = {};

    for (const device of campaign.devices) {
        const dev = devices.find(({ id }: { id: number }) => id === device);

        if (!dev) {
            continue;
        }

        const sign: string = dev.Shop.Sign.name;

        if (sign in signs) {
            signs[sign]++;
        } else {
            signs[sign] = 1;
        }
    }

    Object.keys(signs)
        .sort()
        .forEach((key) => {
            signsOrdered[key] = signs[key];
        });

    return {
        id: campaign._id,
        startDate: new Date(campaign.startDate),
        endDate: new Date(campaign.endDate),
        name: campaign.name,
        originalName: campaign.name,
        planning: campaign.planning,
        devices: campaign.devices,
        slots: campaign.slots,
        signs: signsOrdered,
        creatives: campaign.creatives.map((c: any) => ({ filename: c.filename, id: c._id })),
        active: campaign.active,
        advertiser: campaign.advertiser,
        brand: campaign.brand,
        groupedIds: [],
        groupName: campaign.groupName,
        state: campaign.state || 'passed',
        planningUserId: campaign.planningUserId,
        logoUrl: campaign.logoUrl,
        hideFromInterface: campaign.hideFromInterface,
        revenue: campaign.revenue || 0
    };
};

export const generateCampaignObjects = (campaigns: any, devices: any) => {
    const res: ICampaign[] = [];

    for (const campaign of campaigns) {
        res.push(generateCampaignObject(campaign, devices));
    }

    return res;
};

export const filterCampaignsByMonth = (campaigns: ICampaign[], date: Date) => {
    const start = new Date(date);
    const end = new Date(date);

    start.setDate(1);
    end.setDate(1);
    end.setMonth(end.getMonth() + 1);
    end.setDate(end.getDate() - 1);

    return campaigns.filter(({ startDate, endDate }) => startDate <= end && endDate >= start);
};

export const computeCampaignPercentage = (campaign: ICampaign, day: number, maxDevices: number) => {
    const hoursTaken = campaign.planning[day].length;
    const slotsTaken = campaign.slots;
    const devicesTaken = campaign.devices.length;

    return (slotsTaken / MAX_SLOTS) * (hoursTaken / MAX_HOURS) * (devicesTaken / maxDevices);
};

export const computeCampaignsPercentage = (campaigns: ICampaign[], day: number, maxDevices: number) => {
    let percentage = 0;

    for (const c of campaigns) {
        percentage += computeCampaignPercentage(c, day, maxDevices);
    }

    return Math.round(10000 * percentage) / 100;
};

export const genFullDevicePercentagePerHour = (campaigns: ICampaign[], startDate: Date) => {
    const hoursDevices: { [key: number]: number }[][] = new Array(7)
        .fill(null)
        .map(() => new Array(MAX_HOURS).fill(null).map(() => ({})));

    for (const campaign of campaigns) {
        const start = new Date(startDate);

        for (let i = 0; i < 7; i++) {
            const isCampaignFinished = campaign.endDate < start;
            const isCampaignStarted = campaign.startDate <= start;

            for (let j = 7; j <= 22; j++) {
                if (isCampaignStarted && !isCampaignFinished && campaign.planning[i].includes(j)) {
                    for (const d of campaign.devices) {
                        if (d in hoursDevices[i][j - 7]) {
                            hoursDevices[i][j - 7][d] += campaign.slots;
                        } else {
                            hoursDevices[i][j - 7][d] = campaign.slots;
                        }
                    }
                }
            }

            start.setDate(start.getDate() + 1);
        }
    }

    const devicesFull: number[][][] = new Array(7)
        .fill(null)
        .map(() => new Array(16).fill(null).map(() => new Array(0)));

    for (let i = 0; i < 7; i++) {
        for (let j = 0; j < MAX_HOURS; j++) {
            devicesFull[i][j] = Object.keys(hoursDevices[i][j])
                .filter((k) => hoursDevices[i][j][parseInt(k)] >= MAX_SLOTS)
                .map((k) => parseInt(k));
        }
    }

    return devicesFull;
};

const arrayEquals = <T>(arr1: T[], arr2: T[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }

    return true;
};

export const mergeBlocks = (data: number[][]) => {
    const output = [
        {
            id: 0,
            hoursNb: 1,
            devices: data[0]
        }
    ];

    let i = 1;
    let cursor = output[0];

    for (; i < data.length; i++) {
        if (arrayEquals(data[i], cursor.devices)) {
            cursor.hoursNb++;
        } else {
            const len = output.push({
                id: i,
                hoursNb: 1,
                devices: data[i]
            });

            cursor = output[len - 1];
        }
    }

    return output;
};

export const mergeBinaryBlock = (data: boolean[]) => {
    const output = [
        {
            id: 0,
            hoursNb: 1,
            isCamp: data[0]
        }
    ];

    let i = 1;
    let cursor = output[0];

    for (; i < data.length; i++) {
        if (data[i] === cursor.isCamp) {
            cursor.hoursNb++;
        } else {
            const len = output.push({
                id: i,
                hoursNb: 1,
                isCamp: data[i]
            });

            cursor = output[len - 1];
        }
    }

    return output;
};

export const groupCampaigns = (o_campaigns: ICampaign[]) => {
    const campaigns = [...o_campaigns.map((c) => ({ ...c, groupedIds: [...c.groupedIds] }))];

    const planningEquals = (arr1: number[][], arr2: number[][]) => {
        for (let i = 0; i < 7; i++) {
            if (arr1[i].length !== arr2[i].length) {
                return false;
            }

            for (let j = 0; j < arr1[i].length; j++) {
                if (arr1[i][j] !== arr2[i][j]) {
                    return false;
                }
            }
        }

        return true;
    };

    const devicesUnionNoDuplicates = (c1: number[], c2: number[]) => {
        const s = new Set(c1);

        for (const e of c2) {
            if (s.has(e)) {
                return false;
            }
        }

        return true;
    };

    for (let i = 0; i < campaigns.length; i++) {
        campaigns[i].groupedIds = [];
    }

    for (let i = 0; i < campaigns.length; i++) {
        for (let j = i + 1; j < campaigns.length; j++) {
            if (
                campaigns[j].advertiser === campaigns[i].advertiser &&
                campaigns[j].brand === campaigns[i].brand &&
                campaigns[i].state !== 'draft' &&
                campaigns[j].state !== 'draft' &&
                planningEquals(campaigns[i].planning, campaigns[j].planning) &&
                campaigns[j].groupName &&
                campaigns[i].groupName &&
                campaigns[j].groupName === campaigns[i].groupName &&
                devicesUnionNoDuplicates(campaigns[i].devices, campaigns[j].devices) &&
                campaigns[i].startDate.getTime() === campaigns[j].startDate.getTime()
            ) {
                campaigns[i].groupedIds.push(campaigns[j].id);
                campaigns[j].groupedIds.push(campaigns[i].id);
            }
        }
    }

    return campaigns;
};

export const flattenCampaigns = (campaigns: ICampaign[], flatten: boolean) => {
    if (!flatten) {
        return campaigns;
    }

    const flattenedCampaigns = [...campaigns.map((c) => ({ ...c }))];

    for (let i = 0; i < flattenedCampaigns.length; i++) {
        if (flattenedCampaigns[i].groupedIds.length > 0) {
            // Merge
            flattenedCampaigns[i].name =
                flattenedCampaigns[i].groupName || flattenedCampaigns[i].brand || flattenedCampaigns[i].name;

            for (let j = 0; j < flattenedCampaigns.length; j++) {
                if (flattenedCampaigns[i].groupedIds.includes(flattenedCampaigns[j].id)) {
                    flattenedCampaigns[i].devices = [
                        ...flattenedCampaigns[i].devices,
                        ...flattenedCampaigns[j].devices
                    ];
                    flattenedCampaigns.splice(j, 1);
                    j--;
                }
            }
        } else if (flattenedCampaigns[i].groupName !== undefined) {
            flattenedCampaigns[i].name = flattenedCampaigns[i].groupName as string;
        }
    }

    return flattenedCampaigns;
};

export const downloadExcelDevices = (deviceList: IDevice[]) => {
    // const rows = [['Device #', 'Magasin', 'Enseigne']];

    downloadScreenList(deviceList, []);
    // for (const d of deviceList) {
    //     if (d && (d as any).Shop) rows.push([d.id, (d as any).Shop.name, (d as any).Shop.Sign.name]);
    // }

    // let data = '';
    // for (let i = 0; i < rows.length; i++) {
    //     data += rows[i][0];

    //     for (let c = 1; c < rows[0].length; c++) {
    //         data += ',' + rows[i][c];
    //     }

    //     data += '\n';
    // }

    // const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
    // const pom = document.createElement('a');
    // const url = URL.createObjectURL(blob);
    // pom.href = url;
    // pom.setAttribute('download', 'export.csv');
    // pom.click();
    // URL.revokeObjectURL(url);
};

export const arrayEqual = <T>(arr1: T[], arr2: T[]) => {
    if (arr1.length !== arr2.length) {
        return false;
    }

    for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
            return false;
        }
    }

    return true;
};

export const addCampaignToArray = (array: ICampaign[], newCampaign: ICampaign) => [...array, newCampaign];
export const updateCampaignFromArray = (array: ICampaign[], updatedCampaign: ICampaign) => {
    const index = array.findIndex(({ id }) => id === updatedCampaign.id);
    if (index === -1) {
        return array;
    }

    const newArray = [...array];
    newArray.splice(index, 1, updatedCampaign);

    return newArray;
};
export const removeCampaignFromArray = (array: ICampaign[], removedCampaign: ICampaign) =>
    array.filter(({ id }) => id !== removedCampaign.id);
